import { Row, Col, Checkbox, Skeleton } from "antd";
import { Form } from "@dreambigger/design-system/src/components";
import { TermsStepAssets } from "@dreambigger/shared/src/types";
import { StepProps } from "../pages/flows/[flowId]";
import StepWrapper from "./step-wrapper";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./steps.module.scss";
import { useApplication } from "../api";

const nearBottom = (target: HTMLDivElement) => {
  return target.scrollHeight - target.scrollTop === target.clientHeight;
};

export const confirmationKey = "termsConfirmation";

export default function Terms({ flow, step, brand, progress }: StepProps) {
  const [form] = Form.useForm();
  const [disabled, setDisabled] = useState<boolean>(true);
  const [checkboxDisabled, setCheckboxDisabled] = useState<boolean>(true);
  const [loaded, setLoaded] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);
  const applicationHelper = useApplication(flow.financialInstitution.id);
  const application = useMemo(
    () => applicationHelper.find(flow.id, "draft"),
    [applicationHelper, flow]
  );
  const response = useMemo(
    () => applicationHelper.findResponse(flow.id, "draft", step.slug),
    [application, flow, step]
  );

  // Set the correct disabled state and prefill the checkbox if needed after application retrieval
  useEffect(() => {
    if (!application) return;
    const confirmationValue = response?.fields[confirmationKey];
    setDisabled(confirmationValue !== true);
    setCheckboxDisabled(confirmationValue !== true);
    form.setFieldsValue({
      [confirmationKey]: confirmationValue,
    });
    setLoaded(true);
  }, [application, response, form]);

  // Enable the appropriate controls after the user has scrolled to the bottom of the terms.
  const trackScrolling = useCallback(() => {
    if (ref.current && nearBottom(ref.current)) {
      document.removeEventListener("scroll", trackScrolling);
      setCheckboxDisabled(false); // Enable the checkbox (only matters if confirmationText is present)
      !assets.confirmationText && setDisabled(false); // If no confirmation text, enable the continue button
    }
  }, [ref, setDisabled]);

  useEffect(() => {
    trackScrolling();
  }, [trackScrolling]);

  const wrapperProps = {
    step,
    brand,
    flow,
    progress,
    form,
    disabled,
  };
  const assets: TermsStepAssets = step.assets;

  return (
    <StepWrapper {...wrapperProps}>
      <Row>
        <Col xs={24}>
          <label>{assets.termsTitle}</label>
          {!loaded ? (
            <Skeleton.Input
              className="mb-2 w-100"
              active={true}
              style={{ height: "40vh" }}
            />
          ) : (
            <>
              <div
                className="bt bb mv-2 pa-3"
                style={{
                  borderColor: "#E4E4E4",
                  maxHeight: "40vh",
                  overflowX: "hidden",
                  overflowY: "auto",
                }}
                ref={ref}
                onScroll={trackScrolling}
              >
                <p
                  className={`${styles.textLinks} f-4 gray-8 lh-4`}
                  dangerouslySetInnerHTML={{
                    __html: assets.termsText ?? "",
                  }}
                />
              </div>
              {assets.confirmationText && (
                <div className="mt-6 flex items-center">
                  <Form.Item
                    name={confirmationKey}
                    valuePropName="checked"
                    className="mb-0 mr-3"
                  >
                    <Checkbox
                      className="ao-lg-checkbox"
                      disabled={checkboxDisabled}
                      onClick={() => setDisabled(!disabled)}
                    />
                  </Form.Item>
                  <p
                    className={`${styles.textLinks} f-4 ${
                      checkboxDisabled ? "warning notAllowed" : "gray-8"
                    } lh-4 mb-0`}
                    dangerouslySetInnerHTML={{
                      __html: checkboxDisabled
                        ? "Please read the terms to continue."
                        : assets.confirmationText,
                    }}
                  />
                </div>
              )}
            </>
          )}
        </Col>
      </Row>
    </StepWrapper>
  );
}
